import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, MapPin } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { getGalleries } from "../../slice/gallerySlice";
import { getMediaUrl } from "../../common/mediaUrl";

const ScrollRevealGallery = () => {
  const [selectedImg, setSelectedImg] = useState(null);
  const dispatch = useDispatch();
  const { data: galleries, loading } = useSelector((state) => state.gallery);

  useEffect(() => {
    document.title = 'Gallery | Khajuraho Routes';
    window.scrollTo(0, 0);
    dispatch(getGalleries());
  }, [dispatch]);

  const getImageUrl = (imgJson) => {
    try {
      const images = JSON.parse(imgJson);
      const firstImg = images[0];
      return getMediaUrl(firstImg);
    } catch (e) {
      return '';
    }
  };

  const gridSpans = [
    "col-span-1 row-span-1 md:col-span-2 md:row-span-2",
    "col-span-1 row-span-1",
    "col-span-1 row-span-1",
    "col-span-1 row-span-1 md:col-span-2 md:row-span-1",
    "col-span-1 row-span-1",
    "col-span-1 row-span-1 md:row-span-2",
    "col-span-1 row-span-1",
    "col-span-1 row-span-1",
  ];

  return (
    <main className="w-full bg-[#fbf4e8] min-h-screen">
      {/* ─── HEADER SECTION ─── */}
      <section className="pt-16 pb-16 px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-orange-main/30 bg-orange-main/10">
            <span className="w-2 h-2 rounded-full bg-orange-main animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest text-orange-main">
              Visual Journey
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 tracking-tight">
            Our <span className="text-orange-main">Gallery</span>
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            Take a visual tour through the magnificent temples of Khajuraho, the wild landscapes of Panna, and the historic charm of central India.
          </p>
        </motion.div>
      </section>

      {/* ─── GALLERY GRID ─── */}
      <section className="px-6 md:px-8 pb-32 max-w-[1400px] mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-20">
             <div className="w-12 h-12 border-4 border-orange-main border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-4 auto-rows-[280px] sm:auto-rows-[200px] md:auto-rows-[220px]">
            {galleries && galleries.length > 0 ? (
              galleries.map((img, index) => {
                const spanClass = gridSpans[index % gridSpans.length];
                const imgSrc = getImageUrl(img.images);
                return (
                  <motion.div
                    key={img.id || index}
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6, delay: (index % 4) * 0.1 }}
                    className={`relative rounded-3xl overflow-hidden group cursor-default md:cursor-pointer shadow-xl hover:shadow-2xl transition-all border-4 border-white ${spanClass}`}
                    onClick={() => {
                      if (window.innerWidth >= 1024) {
                        setSelectedImg({ ...img, src: imgSrc });
                      }
                    }}
                  >
                    {/* IMAGE */}
                    <img
                      src={imgSrc}
                      alt={img.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    
                    {/* HOVER OVERLAY */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                      <div className="transform translate-y-0 lg:translate-y-4 lg:group-hover:translate-y-0 transition-transform duration-300">
                        <div className="flex justify-between items-end">
                          <div className="flex-1">
                            <h3 className="text-white font-black text-lg md:text-xl mb-1 leading-tight">{img.title}</h3>
                            <div className="flex items-center gap-1.5 text-orange-main text-[10px] md:text-xs font-black uppercase tracking-widest">
                              <MapPin size={12} strokeWidth={3} /> {img.location || 'Khajuraho, MP'}
                            </div>
                          </div>
                          <div className="hidden lg:flex w-10 h-10 rounded-2xl bg-orange-main items-center justify-center text-white shrink-0 shadow-lg shadow-orange-main/40">
                            <ZoomIn size={18} strokeWidth={3} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <div className="col-span-full py-20 text-center text-gray-500 font-bold">
                No gallery images found.
              </div>
            )}
          </div>
        )}
      </section>

      {/* ─── LIGHTBOX MODAL ─── */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 md:pt-20 bg-black/95 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelectedImg(null)}
          >
            {/* CLOSE BUTTON */}
            <button 
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-orange-main text-white rounded-full flex items-center justify-center transition-colors z-[60]"
              onClick={() => setSelectedImg(null)}
            >
              <X size={24} />
            </button>

            {/* MODAL CONTENT */}
            <motion.div
              initial={{ scale: 0.9, y: -100, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: -100, opacity: 0 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="relative max-w-5xl w-full flex flex-col items-center pb-12"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImg.src}
                alt={selectedImg.title}
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border-4 border-white/10"
              />
              <div className="mt-8 text-center bg-black/40 backdrop-blur-md px-8 py-4 rounded-3xl border border-white/10">
                <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight">{selectedImg.title}</h3>
                <div className="flex items-center justify-center gap-2 mt-2 text-orange-main text-sm font-black tracking-widest uppercase">
                  <MapPin size={16} strokeWidth={3} /> {selectedImg.location || 'Khajuraho Routes Exclusive'}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default ScrollRevealGallery;
